/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode, useCallback, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { enrichResultTitle } from '@atlassian/dynamic-cards-search-common';

import { useFeatures } from '../../../controllers/store';
import { useAppContext } from '../app-context';

import { messages } from './messages';
import { type EventWithTarget } from './types';

const searchLinkStyles = xcss({
	width: 'fill',
	padding: 'space.100',
	display: 'grid',
	columnGap: 'space.100',
	gridTemplateColumns: 'auto 1fr auto',
	gridTemplateAreas: `
    "icon title subheading"
  `,
	alignItems: 'center',
	height: '40px',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	borderRadius: 'radius.large',
	cursor: 'pointer',
});

const searchLinkWithDescriptionStyles = xcss({
	width: 'fill',
	padding: 'space.100',
	display: 'grid',
	columnGap: 'space.100',
	gridTemplateColumns: 'auto 1fr',
	gridTemplateAreas: `
    "icon title subheading"
    "icon description subheading"
  `,
	alignItems: 'center',
	height: '50px',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	borderRadius: 'radius.large',
	cursor: 'pointer',
});

const iconStyles = xcss({
	gridArea: 'icon',
});

const titleStyles = xcss({
	gridArea: 'title',
	color: 'color.text',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	font: 'font.body',
});

const descriptionStyles = xcss({
	gridArea: 'description',
	display: 'flex',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	font: 'font.body.small',
	color: 'color.text.subtle',
	paddingTop: 'space.075',
});

const subheadingStyles = xcss({
	gridArea: 'subheading',
	display: 'flex',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	font: 'font.body.small',
	color: 'color.text.subtle',
	alignItems: 'center',
	gap: 'space.050',
});

export type SearchLinkProps = {
	asBox?: boolean;
	description?: string | JSX.Element;
	highlightedTitle?: string;
	icon: ReactNode;
	isKeyboardHighlighted?: boolean;
	navigationUrl?: string;
	onClick: (event: EventWithTarget) => void;
	rightPanel?: ReactNode;
	subheading?: string | JSX.Element;
	title: string;
};

const DropdownResultContent = ({
	title,
	description,
	subheading,
	icon,
	onClick,
	highlightedTitle,
	rightPanel,
	asBox = false,
	isKeyboardHighlighted = false,
}: SearchLinkProps) => {
	const { formatMessage } = useIntl();

	const [isMouseHighlighted, setIsMouseHighlighted] = useState(false);

	if (asBox) {
		return (
			<Box
				xcss={description ? searchLinkWithDescriptionStyles : searchLinkStyles}
				testId="search-page-dropdown-result"
			>
				<Box xcss={iconStyles}>{icon}</Box>
				<Box xcss={titleStyles}>
					{highlightedTitle ? enrichResultTitle(highlightedTitle) : title}
				</Box>
				{subheading && <Box xcss={subheadingStyles}>{subheading}</Box>}
				{description && <Box xcss={descriptionStyles}>{description}</Box>}
				{(isKeyboardHighlighted || isMouseHighlighted) && rightPanel}
			</Box>
		);
	}

	return (
		<Pressable
			xcss={description ? searchLinkWithDescriptionStyles : searchLinkStyles}
			backgroundColor={
				isKeyboardHighlighted
					? 'color.background.neutral.subtle.hovered'
					: 'color.background.neutral.subtle'
			}
			onClick={onClick}
			onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()}
			aria-label={formatMessage(messages.searchLinkLabel)}
			onMouseOver={() => setIsMouseHighlighted(true)}
			onMouseLeave={() => setIsMouseHighlighted(false)}
			testId="search-page-dropdown-result"
		>
			<Box xcss={iconStyles}>{icon}</Box>
			<Box xcss={titleStyles}>{highlightedTitle ? enrichResultTitle(highlightedTitle) : title}</Box>
			{subheading && <Box xcss={subheadingStyles}>{subheading}</Box>}
			{description && <Box xcss={descriptionStyles}>{description}</Box>}
			{(isKeyboardHighlighted || isMouseHighlighted) && rightPanel}
		</Pressable>
	);
};

export const DropdownResultLink = (props: SearchLinkProps): JSX.Element => {
	const { linkComponent: Link } = useAppContext();

	return (
		<Link
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={{ textDecoration: 'none', color: 'inherit' }}
			backgroundColor="color.background.neutral.subtle"
			href={props.navigationUrl || ''}
		>
			<DropdownResultContent {...props} asBox={true} />
		</Link>
	);
};

export const DropdownResultChromeExtension = (props: SearchLinkProps): JSX.Element => {
	const [features] = useFeatures();

	const onClick = useCallback(() => {
		if (!features?.chromeExtension?.enabled) {
			return;
		}

		const { onInputDropdownResultSelect } = features.chromeExtension;
		onInputDropdownResultSelect({ title: props.title, url: props.navigationUrl });
	}, [features?.chromeExtension, props.title, props.navigationUrl]);

	return <DropdownResultContent {...props} onClick={onClick} />;
};

export const SearchLink = (props: SearchLinkProps): JSX.Element => {
	const [features] = useFeatures();

	if (features?.chromeExtension?.enabled) {
		return <DropdownResultChromeExtension {...props} />;
	} else if (props.navigationUrl) {
		return <DropdownResultLink {...props} />;
	} else {
		return <DropdownResultContent {...props} />;
	}
};
